import { UniButton } from '@/design-system/components/buttons';
import { H6, TextBody, TextCaption, TextTitle } from '@/design-system/components/typography';
import { SxProp, View, useDripsyTheme, styled } from 'dripsy';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/design-system/components/icons/Icon';
import { Row } from '@/design-system/components/layout';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { AccountsTransfersStackParamList } from '@/features/account-management/screens/accounts-transfers/type';
import moment from 'moment-timezone';
import { PageHeaderModalNative } from '@/features/account-management/screens/page/PageHeaderModalNative';
import { formatAmountByDecimal } from '@/features/account-management/utils';
import { LayoutContext } from 'contexts/LayoutContext';

export interface AccountsTransfersProps {
  onClose: () => void;
  account: string;
  tab?: number;
  data: any;
  returnData?: any;
  refresh?: () => void;
  record?: any;
}

export const AccountsTransfersNativeTwoScreen: React.FC<AccountsTransfersProps> = (props) => {
  const { onClose, account, tab, data, returnData, refresh, record } = props;
  const { t } = useTranslation('account-transfer');
  const layout = useContext(LayoutContext);
  const {
    topHeader,
    body,
    horizontalWrapper,
    textLabel,
    textDebitAmount,
    textTitle,
    textAtmPin,
    btnView,
    btn,
    textHeaderTitle,
    textHeaderSubTitle,
    modal
  } = sxStyles;

  const { theme } = useDripsyTheme();

  const navigation = useNavigation<NativeStackNavigationProp<AccountsTransfersStackParamList>>();

  const [instructionTime, setInstructionTime] = useState(moment().format('YYYY-MM-DD HH:mm:ss'));
  const viewHight = layout.dimensions.height > 618 ? layout.dimensions.height - 386 : undefined;

  return (
    <View sx={{ height: '100%', backgroundColor: '$white' }}>
      <PageHeaderModalNative>
        <Row sx={{ justifyContent: 'center', alignItems: 'center', px: '$4', mt: '$5', mb: '$4' }}>
          <View sx={{ textAlign: 'center' }}>
            <TextTitle bold sx={{ color: theme?.colors?.$white as string }}>
              {t('account-transfer::instruction-form.title--account-transfer')}
            </TextTitle>
          </View>
        </Row>
      </PageHeaderModalNative>
      <View sx={modal}>
        <View sx={topHeader}>
          <Icon name="checkCircleBlue" height={108} width={108} />
          <H6 bold sx={textHeaderTitle}>
            {t('account-transfer::instruction-placed.text--account-transfer-placed')}
          </H6>
          <TextCaption sx={textHeaderSubTitle}>{t('account-transfer::instruction-placed.text--you-made-following-transfer')}</TextCaption>
        </View>
        <View sx={body}>
          <View sx={{ gap: '$5', height: viewHight }}>
            <View>
              <TextCaption sx={textTitle} semiBold>
                {t('account-transfer::glossary.from-account')}
              </TextCaption>
              <H6 sx={{ ...textAtmPin, mb: '$1' }}>
                {data?.sourceAccount?.accountLabel
                  ? t(`earn-invest::glossary.${data?.sourceAccount?.accountLabel}`)
                  : data?.sourceAccount?.accountName || ' - '}
              </H6>
              <TextBody>{data.sourceAccount.accountNumber}</TextBody>
            </View>
            <View>
              <TextCaption sx={textTitle} semiBold>
                {t('account-transfer::glossary.to-account')}
              </TextCaption>
              <H6 sx={{ ...textAtmPin, mb: '$1' }}>
                {data?.destinationAccount?.accountLabel
                  ? t(`earn-invest::glossary.${data?.destinationAccount?.accountLabel}`)
                  : data?.destinationAccount?.accountName || ' - '}
              </H6>
              <TextBody>{data.destinationAccount.accountNumber}</TextBody>
            </View>
            <View>
              <TextCaption sx={textTitle} semiBold>
                {t('account-transfer::glossary.amount')}
              </TextCaption>
              <H6 sx={textAtmPin}>
                {formatAmountByDecimal({ value: data.amount, decimals: 2 })} {data?.asset}
              </H6>
            </View>
            <View>
              <TextCaption sx={textTitle} semiBold>
                {t('account-transfer::glossary.description-optional')}
              </TextCaption>
              <H6 sx={textAtmPin}>{data.description || '-'}</H6>
            </View>
            <View>
              <TextCaption sx={textTitle} bold>
                {t('card-account-management::glossary.xmetadata')}
              </TextCaption>
              {Object.keys(data.xmetadata).map((k) => (
                <View sx={{ pl: '$5', my: '$3' }}>
                  <TextCaption sx={textTitle} semiBold>
                    {k}
                  </TextCaption>
                  <H6 sx={textAtmPin}>{data.xmetadata[k]}</H6>
                </View>
              ))}
            </View>
            <View sx={horizontalWrapper}>
              <TextBody sx={textLabel}>{t('account-transfer::glossary.instruction-time')}</TextBody>
              <TextBody sx={textDebitAmount}>{returnData?.instructionTime || instructionTime || '-'}</TextBody>
            </View>
          </View>
          <View sx={btnView}>
            <UniButton
              sx={btn}
              labelSx={{ color: '$balck' }}
              label={t('account-transfer::glossary.button-close')}
              onPress={() => {
                refresh && refresh();
                navigation.getParent()?.goBack();
              }}
            />
          </View>
        </View>
      </View>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  modal: {
    padding: '$5'
  },
  topHeader: {
    justifyContent: 'center',
    alignItems: 'center',
    background: 'primaryDefault'
  },
  body: {
    py: 28
  },
  horizontalWrapper: {
    flexDirection: 'column',
    alignItems: 'flex-start'
  },
  textLabel: {
    color: '$textSecondary',
    mb: '$1'
  },
  textDebitAmount: {
    mb: '$1',
    color: '$textPrimary'
  },
  textTitle: {
    marginBottom: 4,
    color: '$textSecondary'
  },
  textAtmPin: {
    fontWeight: 'bolder',
    color: '$textThemeSecondary'
  },
  btnView: {
    flex: 1,
    display: 'grid',
    gap: '$3',
    mt: '$4'
  },
  btn: {
    width: '100%',
    height: 44
  },
  textHeaderTitle: {
    mb: '$2',
    textAlign: 'center'
  },
  textHeaderSubTitle: {
    color: '$textPrimary'
  },
  timeView: {
    mb: '$3',
    mt: '$5'
  },
  refView: {
    mb: '$10'
  }
};
